import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { patients, staff, insurance, assessments } from '../services/api';

const PatientAdd = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [therapists, setTherapists] = useState([]);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [assessmentData, setAssessmentData] = useState({
    protocolId: '',
    assessmentFile: null,
    comments: ''
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    primaryDiagnosis: '',
    secondaryDiagnoses: [],
    primaryTherapist: '',
    insuranceCompanyId: '',
    status: 'active',
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    startDate: new Date().toISOString().split('T')[0],
    emergencyContact: '',
    emergencyPhone: '',
    address: '',
    allergies: '',
    medications: '',
    notes: '',
    weeklyHours: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch therapists using the correct method
        const therapistsResponse = await staff.getAllTherapists();
        setTherapists(therapistsResponse.data.data);

        // Fetch insurance providers
        const insuranceResponse = await insurance.getAll();
        setInsuranceProviders(insuranceResponse.data.data);

        // Fetch assessment protocols
        const protocolsResponse = await assessments.getProtocols();
        setProtocols(protocolsResponse.data.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Falha ao carregar dados necessários');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssessmentChange = (e) => {
    const { name, value } = e.target;
    setAssessmentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setAssessmentData(prev => ({
      ...prev,
      assessmentFile: file
    }));
  };

  const handleAnalyzeAssessment = async () => {
    if (!assessmentData.assessmentFile || !assessmentData.protocolId) {
      setError('Please select both assessment type and file before analyzing');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // First upload the assessment
      const formData = new FormData();
      formData.append('assessmentFile', assessmentData.assessmentFile);
      formData.append('protocolId', assessmentData.protocolId);
      formData.append('comments', assessmentData.comments || '');
      
      const uploadResponse = await assessments.upload('temp', formData);
      const assessmentId = uploadResponse.data.data.id;
      
      // Then analyze it
      const analyzeResponse = await assessments.analyze(assessmentId);
      
      setSuccess('Assessment analyzed successfully! Text extracted and PII cleaned.');
      
      // Clear the form
      setAssessmentData({
        protocolId: '',
        assessmentFile: null,
        comments: ''
      });
      
    } catch (error) {
      console.error('Error analyzing assessment:', error);
      setError('Failed to analyze assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const submissionData = {
        ...formData,
        gender: formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1).toLowerCase(),
        primaryTherapist: formData.primaryTherapist || null,
        insuranceCompanyId: formData.insuranceCompanyId || null,
        parentEmail: formData.parentEmail || null,
        weeklyHours: formData.weeklyHours ? parseInt(formData.weeklyHours) : null
      };
      const response = await patients.create(submissionData);
      
      // If assessment file is uploaded, upload it after patient creation
      if (assessmentData.assessmentFile && assessmentData.protocolId) {
        try {
          const formData = new FormData();
          formData.append('assessmentFile', assessmentData.assessmentFile);
          formData.append('protocolId', assessmentData.protocolId);
          formData.append('comments', assessmentData.comments);
          
          await assessments.upload(response.data.data.id, formData);
        } catch (assessmentError) {
          console.error('Error uploading assessment:', assessmentError);
          // Don't fail the entire operation if assessment upload fails
        }
      }
      
      navigate('/dashboard/patients', { state: { refresh: true } });
    } catch (error) {
      console.error('Error creating patient:', error);
      setError(error.response?.data?.message || 'Falha ao criar paciente');
      setLoading(false);
    }
  };

  const Notification = ({ type, message }) => {
    if (!message) return null;
    
    const styles = {
      success: 'bg-green-50 text-green-800 border-green-200',
      error: 'bg-red-50 text-red-800 border-red-200'
    };

    return (
      <div className={`p-4 mb-4 rounded-lg border ${styles[type]}`}>
        {message}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/dashboard/patients')}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Adicionar Novo Paciente</h1>
            <p className="text-gray-600">Criar um novo registro de paciente</p>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <Notification type="error" message={error} />
      <Notification type="success" message={success} />

      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Patient Identification */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Identificação do Paciente</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Início*
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Informações Pessoais</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sobrenome
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Nascimento
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gênero*
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Selecione o gênero</option>
                  <option value="Male">Masculino</option>
                  <option value="Female">Feminino</option>
                  <option value="Other">Outro</option>
                </select>
              </div>
            </div>
          </div>

          {/* Parent/Guardian Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Informações do Responsável</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Responsável
                </label>
                <input
                  type="text"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone do Responsável
                </label>
                <input
                  type="tel"
                  name="parentPhone"
                  value={formData.parentPhone}
                  onChange={handleChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email do Responsável
                </label>
                <input
                  type="email"
                  name="parentEmail"
                  value={formData.parentEmail}
                  onChange={handleChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Contato de Emergência</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Contato de Emergência
                </label>
                <input
                  type="text"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone do Contato de Emergência
                </label>
                <input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Informações Médicas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Diagnóstico Primário
                </label>
                <input
                  type="text"
                  name="primaryDiagnosis"
                  value={formData.primaryDiagnosis}
                  onChange={handleChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Diagnósticos Secundários
                </label>
                <input
                  type="text"
                  name="secondaryDiagnoses"
                  value={formData.secondaryDiagnoses.join(', ')}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    secondaryDiagnoses: e.target.value.split(',').map(d => d.trim()).filter(Boolean)
                  }))}
                  placeholder="Separe com vírgulas"
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
          </div>

          {/* Care Team */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Equipe de Cuidados</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Terapeuta Primário
                </label>
                <select
                  name="primaryTherapist"
                  value={formData.primaryTherapist}
                  onChange={handleChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Selecione o terapeuta</option>
                  {therapists.map(therapist => (
                    <option key={therapist.id} value={therapist.id}>
                      {`${therapist.firstName} ${therapist.lastName}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Horas Semanais de Terapia
                </label>
                <input
                  type="number"
                  name="weeklyHours"
                  value={formData.weeklyHours}
                  onChange={handleChange}
                  min="1"
                  max="168"
                  placeholder="Ex: 20"
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
          </div>

          {/* Insurance Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Informações de Seguro</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fornecedor de Seguro
                </label>
                <select
                  name="insuranceCompanyId"
                  value={formData.insuranceCompanyId}
                  onChange={handleChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Selecione o fornecedor</option>
                  {insuranceProviders.map(provider => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Informações Adicionais</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allergies
                </label>
                <textarea
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                  rows={2}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Medications
                </label>
                <textarea
                  name="medications"
                  value={formData.medications}
                  onChange={handleChange}
                  rows={2}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
          </div>

          {/* Assessment Upload Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Assessment Protocol (Optional)
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Upload assessment results to enable AI-powered analysis
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assessment Type
                </label>
                <select
                  name="protocolId"
                  value={assessmentData.protocolId}
                  onChange={handleAssessmentChange}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Select Assessment Type</option>
                  {protocols.map((protocol) => (
                    <option key={protocol.id} value={protocol.id}>
                      {protocol.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assessment File
                </label>
                <input
                  type="file"
                  name="assessmentFile"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: PDF, DOC, DOCX (Max 10MB)
                </p>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comments (Optional)
              </label>
              <textarea
                name="comments"
                value={assessmentData.comments}
                onChange={handleAssessmentChange}
                rows={2}
                placeholder="Any additional notes about this assessment..."
                className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            
            {/* Analyze Button - Show when file is selected */}
            {assessmentData.assessmentFile && assessmentData.protocolId && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleAnalyzeAssessment}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Analyze Assessment
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Extract text and clean PII from the uploaded assessment
                </p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-4 py-2 bg-brand-700 text-white rounded-lg text-sm font-medium hover:bg-brand-800 disabled:opacity-50"
            >
              <Save size={16} className="mr-2" />
              {loading ? 'Creating...' : 'Create Patient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientAdd; 